// src/context/AuthContext.js - With updated tier terminology
import React, { createContext, useState, useEffect, useContext } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signInWithPopup,
  GoogleAuthProvider,
  signOut, 
  onAuthStateChanged,
  updateProfile
} from "firebase/auth";
import { doc, setDoc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from '../firebase/firebaseConfig';
import { USER_TIERS } from '../services/UserService';
import { getSubscriptionStatus } from '../services/paymentService';

// Create context
export const AuthContext = createContext();

// Custom hook for using the context
export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userMeta, setUserMeta] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  // Explicitly track userTier as a separate state to ensure consistency
  const [userTier, setUserTier] = useState(USER_TIERS.NONE);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setIsAuthReady(true);
      
      // If user is logged in, get user metadata from Firestore
      if (user) {
        try {
          const userDocRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            // Handle potential migration from PREMIUM to PRO tier for existing users
            const userData = userDoc.data();
            if (userData.tier === 'premium') {
              await updateDoc(userDocRef, { tier: USER_TIERS.PRO });
              userData.tier = USER_TIERS.PRO;
            }
            setUserMeta(userData);
            
            // Check Stripe for subscription status
            try {
              const subscriptionData = await getSubscriptionStatus(user.uid, user.email);
              
              // If subscription status from Stripe differs from Firestore, update Firestore
              if (subscriptionData.tier !== userData.tier || 
                  (subscriptionData.subscription && 
                   userData.subscription && 
                   subscriptionData.subscription.id !== userData.subscription.id)) {
                
                await updateDoc(userDocRef, {
                  tier: subscriptionData.tier,
                  subscription: subscriptionData.subscription || userData.subscription
                });
                
                // Update local states
                userData.tier = subscriptionData.tier;
                userData.subscription = subscriptionData.subscription || userData.subscription;
              }
            } catch (stripeError) {
              console.error("Error getting subscription from Stripe:", stripeError);
              // Continue with existing data if Stripe check fails
            }
            
            // Set userTier from userData (potentially updated from Stripe)
            setUserTier(userData.tier || USER_TIERS.BASIC);
          } else {
            // If no metadata exists, create a basic record
            const newUserMeta = {
              tier: USER_TIERS.BASIC,
              createdAt: new Date().toISOString(),
              savedWorkouts: [],
              workoutHistory: [],
              favoriteExercises: [],
              email: user.email,
              name: user.displayName || user.email.split('@')[0],
              photoURL: user.photoURL || null,
              subscription: {
                status: 'inactive',
                tier: USER_TIERS.BASIC,
                createdAt: null,
                currentPeriodEnd: null,
                id: null,
                planId: null
              }
            };
            
            // Check Stripe first to see if the user already has a subscription
            try {
              const subscriptionData = await getSubscriptionStatus(user.uid, user.email);
              
              if (subscriptionData.tier === USER_TIERS.PRO && subscriptionData.subscription) {
                newUserMeta.tier = USER_TIERS.PRO;
                newUserMeta.subscription = subscriptionData.subscription;
              }
            } catch (stripeError) {
              console.error("Error checking new user subscription:", stripeError);
              // Continue with basic tier if Stripe check fails
            }
            
            setUserMeta(newUserMeta);
            setUserTier(newUserMeta.tier);
            await setDoc(userDocRef, newUserMeta);
          }
        } catch (error) {
          console.error("Error getting user metadata:", error);
          // If there's an error, default to BASIC tier for authenticated users
          setUserTier(USER_TIERS.BASIC);
        }
      } else {
        setUserMeta(null);
        // Reset to NONE tier when logged out
        setUserTier(USER_TIERS.NONE);
      }
      
      setIsLoading(false);
    });

    return unsubscribe;
  }, []);

  // Register new user with email/password
  const register = async (email, password, name) => {
    setIsLoading(true);
    setAuthError(null);
    
    try {
      // Create user with Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update profile with name if provided
      if (name) {
        await updateProfile(user, { displayName: name });
      }
      
      // Create user metadata in Firestore
      const userMeta = {
        tier: USER_TIERS.BASIC, // New users start at Basic tier
        createdAt: new Date().toISOString(),
        savedWorkouts: [],
        workoutHistory: [],
        favoriteExercises: [],
        email,
        name: name || email.split('@')[0],
        subscription: {
          status: 'inactive',
          tier: USER_TIERS.BASIC,
          createdAt: null,
          currentPeriodEnd: null,
          id: null,
          planId: null
        }
      };
      
      await setDoc(doc(db, "users", user.uid), userMeta);
      setUserMeta(userMeta);
      // Explicitly set userTier to BASIC
      setUserTier(USER_TIERS.BASIC);
      
      return user;
    } catch (error) {
      setAuthError(error.message || 'Registration failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Login user with email/password
  const login = async (email, password) => {
    setIsLoading(true);
    setAuthError(null);
    
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (error) {
      setAuthError(error.message || 'Login failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Login with Google
  const loginWithGoogle = async () => {
    setIsLoading(true);
    setAuthError(null);
    
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // User data is handled in the auth state change listener
      return result.user;
    } catch (error) {
      setAuthError(error.message || 'Google login failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout user
  const logout = async () => {
    setIsLoading(true);
    setAuthError(null);
    
    try {
      await signOut(auth);
      // Explicitly set userTier to NONE on logout
      setUserTier(USER_TIERS.NONE);
    } catch (error) {
      setAuthError(error.message || 'Logout failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Update user tier
  const updateUserTier = async (tier) => {
    setIsLoading(true);
    setAuthError(null);
    
    try {
      if (!currentUser) throw new Error("No authenticated user");
      
      const userDocRef = doc(db, "users", currentUser.uid);
      await updateDoc(userDocRef, {
        tier: tier
      });
      
      // Update local state
      setUserMeta(prev => ({
        ...prev,
        tier: tier
      }));
      
      // Explicitly update userTier state
      setUserTier(tier);
      
      return { ...userMeta, tier };
    } catch (error) {
      setAuthError(error.message || 'Failed to update subscription');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Create context value
  const value = {
    currentUser,
    userMeta,
    isLoading,
    authError,
    register,
    login,
    loginWithGoogle,
    logout,
    updateUserTier,
    isAuthenticated: !!currentUser,
    // Use the explicit userTier state instead of accessing it from userMeta
    userTier,
    // Add isAuthReady to context value
    isAuthReady
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;